import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { History, RefreshCw, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_VALUE = '__all__';

const ACTION_LABELS = {
  create: 'Created', read: 'Read', update: 'Updated', delete: 'Deleted',
  login: 'Logged in', logout: 'Logged out', approve: 'Approved', reject: 'Rejected',
  submit: 'Submitted', export: 'Exported', import: 'Imported', transition: 'Transitioned',
  comment: 'Commented', upload: 'Uploaded', download: 'Downloaded', generate: 'Generated', send: 'Sent',
};

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-700', generate: 'bg-emerald-100 text-emerald-700',
  update: 'bg-[#dae4c4] text-[#1a1a1a]', transition: 'bg-[#dae4c4] text-[#1a1a1a]',
  delete: 'bg-[#c97a3a]/10 text-[#c97a3a]', reject: 'bg-[#c97a3a]/10 text-[#c97a3a]',
  login: 'bg-[#f5f5f5] text-[#4a4a4a]', logout: 'bg-[#f5f5f5] text-[#4a4a4a]',
  approve: 'bg-emerald-100 text-emerald-700', submit: 'bg-amber-100 text-amber-700',
  send: 'bg-[#ffd87a]/20 text-[#1a1a1a]', download: 'bg-[#f5f5f5] text-[#4a4a4a]', upload: 'bg-[#f5f5f5] text-[#4a4a4a]',
};

const RESOURCE_LABELS = {
  user: 'User', role: 'Role', family_office: 'Family Office', entity: 'Entity',
  deal: 'Deal', investment: 'Investment', document: 'Document', report: 'Report',
  approval: 'Approval', workflow: 'Workflow', startup: 'Startup', valuation: 'Valuation',
  ic_memo: 'IC Memo', system: 'System',
};

const ACTION_OPTIONS = Object.keys(ACTION_LABELS);
const RESOURCE_OPTIONS = Object.keys(RESOURCE_LABELS);

function fmtTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState(ALL_VALUE);
  const [resourceFilter, setResourceFilter] = useState(ALL_VALUE);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      // Always scope to the current user's own family office — this DB is
      // shared across multiple family offices, so an unscoped query would
      // leak other tenants' audit trails into this view.
      const params = { limit: 200, family_office_id: user?.family_office_id };
      if (actionFilter !== ALL_VALUE) params.action = actionFilter;
      if (resourceFilter !== ALL_VALUE) params.resource_type = resourceFilter;
      const res = await axios.get(`${API}/audit/logs`, { params });
      setLogs(res.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, resourceFilter, user?.family_office_id]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = logs.filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.description || '').toLowerCase().includes(q)
      || (l.user_email || '').toLowerCase().includes(q)
      || (l.resource_id || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <History className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Audit Log</h1>
            <p className="text-sm text-[#4a4a4a]">Who did what, and when — across this family office</p>
          </div>
        </div>
        <button
          onClick={fetchLogs}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-[#e5e5e5] rounded-lg hover:border-[#1a1a1a] text-[#4a4a4a] hover:text-[#1a1a1a] transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search description, email, resource id…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e5e5] outline-none focus:border-[#ffd87a] bg-white text-[#1a1a1a] rounded-lg"
          />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[180px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All actions</SelectItem>
            {ACTION_OPTIONS.map(a => <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-[200px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All resource types</SelectItem>
            {RESOURCE_OPTIONS.map(r => <SelectItem key={r} value={r}>{RESOURCE_LABELS[r]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-[#e5e5e5] overflow-hidden rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-[#ffd87a] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <History className="w-10 h-10 text-[#4a4a4a] mx-auto mb-3" />
            <p className="text-sm text-[#4a4a4a]">No audit events found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#dae4c4]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">When</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Who</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Resource</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a] uppercase tracking-wider">Description</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e5e5]">
              {filtered.map((log, idx) => {
                const isOpen = expandedId === log.id;
                const hasDetail = log.metadata && Object.keys(log.metadata).length > 0;
                return (
                  <React.Fragment key={log.id}>
                    <tr
                      className={`transition-colors hover:bg-[#f9f9f9] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} ${hasDetail ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetail && setExpandedId(isOpen ? null : log.id)}
                    >
                      <td className="px-4 py-3 text-xs text-[#4a4a4a] whitespace-nowrap">{fmtTimestamp(log.timestamp)}</td>
                      <td className="px-4 py-3 text-sm text-[#1a1a1a]">{log.user_email || log.user_id || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${ACTION_COLORS[log.action] || 'bg-[#f5f5f5] text-[#4a4a4a]'}`}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#4a4a4a]">{RESOURCE_LABELS[log.resource_type] || log.resource_type}</td>
                      <td className="px-4 py-3 text-sm text-[#1a1a1a]">{log.description || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {hasDetail && (isOpen ? <ChevronUp className="w-4 h-4 text-[#4a4a4a]" /> : <ChevronDown className="w-4 h-4 text-[#4a4a4a]" />)}
                      </td>
                    </tr>
                    {isOpen && hasDetail && (
                      <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'}>
                        <td colSpan={6} className="px-4 pb-3">
                          <pre className="text-[11px] bg-[#f9f9f9] border border-[#e5e5e5] rounded-lg p-3 overflow-x-auto text-[#4a4a4a]">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
